/**
 * AdminEmail — Phase 26
 *
 * Email subsystem control room.
 *   - Provider status card (Resend) with domain verification badge
 *   - 24h KPIs: Sent / Failed / Dry-run
 *   - Send Test Email panel (template dropdown + recipient input)
 *   - Mail log table with filters + HTML preview modal
 *   - "How to verify runidea.ai" expandable section with SPF/DKIM/DMARC records
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Copy,
  ExternalLink,
  Eye,
  Loader2,
  Mail,
  RefreshCw,
  Send,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { key: "welcome", label: "Welcome email" },
  { key: "verification", label: "Email verification (6-digit code)" },
  { key: "password_reset", label: "Password reset" },
  { key: "tenant_invite", label: "Tenant invite" },
  { key: "deal_report_share", label: "Deal report share" },
  { key: "admin_signup_alert", label: "Admin signup alert" },
];

const STATUS_BADGE = {
  sent: "border-emerald-200 bg-emerald-50 text-emerald-800",
  failed: "border-rose-200 bg-rose-50 text-rose-800",
  dry_run: "border-amber-200 bg-amber-50 text-amber-800",
  queued: "border-slate-200 bg-slate-50 text-slate-700",
};

function StatusBadge({ status }) {
  const tone = STATUS_BADGE[status] || "border-slate-200 bg-slate-50 text-slate-600";
  return (
    <span
      data-testid={`mail-log-status-${status}`}
      className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[11px] font-semibold uppercase tracking-wide ${tone}`}
    >
      {status}
    </span>
  );
}

function DomainBadge({ verified, status }) {
  if (verified) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-emerald-200 bg-emerald-50 text-emerald-800 text-xs font-semibold">
        <CheckCircle2 className="w-3.5 h-3.5" />
        Verified
      </span>
    );
  }
  if (status === "pending" || status === "unknown") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-amber-200 bg-amber-50 text-amber-800 text-xs font-semibold">
        <AlertTriangle className="w-3.5 h-3.5" />
        Pending verification
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md border border-rose-200 bg-rose-50 text-rose-800 text-xs font-semibold">
      <XCircle className="w-3.5 h-3.5" />
      Unverified — using onboarding@resend.dev
    </span>
  );
}

export default function AdminEmail() {
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [filterEvent, setFilterEvent] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testTemplate, setTestTemplate] = useState("welcome");
  const [sending, setSending] = useState(false);
  const [previewLog, setPreviewLog] = useState(null);
  const [dnsExpanded, setDnsExpanded] = useState(false);
  const [dnsData, setDnsData] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/email/status"),
        api.get("/admin/email/logs", { params: { limit: 50, event_type: filterEvent || undefined, status: filterStatus || undefined } }),
      ]);
      setStatus(s.data);
      setLogs(l.data?.items || []);
      setLogsTotal(l.data?.total || 0);
      if (!testTo) setTestTo(s.data?.from_address?.match(/<(.+)>/)?.[1] || "");
    } catch (e) {
      toast.error("Could not load email status: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [filterEvent, filterStatus, testTo]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const onRefreshDomain = async () => {
    setRefreshing(true);
    try {
      const r = await api.post("/admin/email/refresh-domain-status");
      toast.success(`Domain status: ${r.data?.status || "unknown"}`);
      await loadAll();
    } catch (e) {
      toast.error("Domain refresh failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setRefreshing(false);
    }
  };

  const onSendTest = async () => {
    if (!testTo || !testTo.includes("@")) {
      toast.error("Enter a valid recipient email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/email/test", { to: testTo, template: testTemplate });
      if (r.data?.ok) {
        if (r.data.status === "dry_run") {
          toast.success(`Dry-run logged (no real send).`);
        } else {
          toast.success(`Sent · Resend id: ${r.data?.resend_id?.slice?.(-8) || "(none)"}`);
        }
      } else {
        toast.error(`Failed: ${r.data?.error || "unknown error"}`);
      }
      await loadAll();
    } catch (e) {
      toast.error("Send failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSending(false);
    }
  };

  const onLoadDns = useCallback(async () => {
    if (dnsData) return;
    try {
      const r = await api.get("/admin/email/domain-records");
      setDnsData(r.data);
    } catch (e) {
      toast.error("Could not load DNS records: " + (e?.response?.data?.detail || e.message));
    }
  }, [dnsData]);

  const kpis = status?.last_24h_counts || { sent: 0, failed: 0, dry_run: 0 };

  const eventTypeOptions = useMemo(() => {
    const set = new Set();
    logs.forEach((l) => l.event_type && set.add(l.event_type));
    return Array.from(set).sort();
  }, [logs]);

  if (loading) {
    return (
      <div data-testid="admin-email-loading" className="flex items-center justify-center p-20">
        <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div data-testid="admin-email-page" className="space-y-6 max-w-7xl">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Email</h1>
          <p className="text-sm text-slate-600 mt-1">Resend transactional email — provider status, mail log, and test sender.</p>
        </div>
        <Button data-testid="admin-email-refresh-domain" variant="outline" size="sm" onClick={onRefreshDomain} disabled={refreshing}>
          {refreshing ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-2" />}
          Refresh domain status
        </Button>
      </div>

      {/* Provider Status card */}
      <div data-testid="admin-email-provider-card" className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-md bg-indigo-50 text-indigo-600">
              <Mail className="w-5 h-5" />
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-900">Resend</div>
              <div className="text-xs text-slate-600 mt-0.5">From: <code className="text-slate-900">{status?.from_address || "—"}</code></div>
              <div className="text-xs text-slate-600 mt-0.5">
                Configured: <span className={status?.configured ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{status?.configured ? "yes" : "no"}</span>
                {" · "}
                Dry-run: <span className={status?.dry_run ? "text-amber-700 font-semibold" : "text-slate-700"}>{status?.dry_run ? "ON" : "off"}</span>
              </div>
            </div>
          </div>
          <DomainBadge verified={status?.domain?.verified} status={status?.domain?.status} />
        </div>

        {/* DNS expandable */}
        {!status?.domain?.verified && (
          <div className="mt-4 border-t border-slate-200 pt-4">
            <button
              data-testid="admin-email-dns-toggle"
              onClick={() => { setDnsExpanded((v) => !v); onLoadDns(); }}
              className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700"
            >
              {dnsExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              How to verify <code>runidea.ai</code>
            </button>
            {dnsExpanded && dnsData && (
              <div className="mt-3 space-y-2" data-testid="admin-email-dns-records">
                <p className="text-xs text-slate-600">Add these 3 records to <strong>{dnsData?.where_to_add || "your DNS"}</strong>, then click <em>Refresh domain status</em>.</p>
                {dnsData.records?.map((r) => (
                  <div key={r.name} className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs font-mono">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 text-[10px] font-semibold">{r.type}</span>
                      <span className="text-slate-900 font-semibold">{r.hostname}</span>
                    </div>
                    <div className="text-slate-700 break-all">{r.value}</div>
                    <div className="text-slate-500 text-[11px] mt-1 font-sans">{r.purpose}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 24h KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-email-kpis">
        <KpiCard label="Sent (24h)" value={kpis.sent} tone="emerald" />
        <KpiCard label="Failed (24h)" value={kpis.failed} tone="rose" />
        <KpiCard label="Dry-run (24h)" value={kpis.dry_run} tone="amber" />
      </div>

      {/* Test send panel */}
      <div data-testid="admin-email-test-panel" className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900 mb-1">Send a test email</h2>
        <p className="text-xs text-slate-600 mb-4">Fires any template to the recipient below. Use this to confirm DKIM, branding, and inbox placement.</p>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_2fr_auto] gap-3">
          <div>
            <Label htmlFor="test-template" className="text-xs font-medium text-slate-700">Template</Label>
            <select
              data-testid="admin-email-template-select"
              id="test-template"
              value={testTemplate}
              onChange={(e) => setTestTemplate(e.target.value)}
              className="mt-1 w-full h-9 px-3 rounded-md border border-slate-300 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-600"
            >
              {TEMPLATES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="test-to" className="text-xs font-medium text-slate-700">To</Label>
            <Input data-testid="admin-email-test-to" id="test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" />
          </div>
          <div className="flex items-end">
            <Button data-testid="admin-email-send-test-btn" onClick={onSendTest} disabled={sending} className="w-full md:w-auto">
              {sending ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-2" />}
              Send test
            </Button>
          </div>
        </div>
      </div>

      {/* Mail log table */}
      <div data-testid="admin-email-log-table" className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="flex flex-wrap items-center gap-3 p-4 border-b border-slate-200">
          <h2 className="text-sm font-semibold text-slate-900">Mail log <span className="text-slate-500 font-normal">· {logsTotal} total</span></h2>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <select data-testid="admin-email-filter-event" value={filterEvent} onChange={(e) => setFilterEvent(e.target.value)} className="h-8 px-2 rounded-md border border-slate-300 text-xs">
              <option value="">All events</option>
              {eventTypeOptions.map((et) => <option key={et} value={et}>{et}</option>)}
            </select>
            <select data-testid="admin-email-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="h-8 px-2 rounded-md border border-slate-300 text-xs">
              <option value="">All status</option>
              <option value="sent">sent</option>
              <option value="failed">failed</option>
              <option value="dry_run">dry_run</option>
            </select>
            <Button data-testid="admin-email-log-refresh" variant="outline" size="sm" onClick={loadAll}><RefreshCw className="w-3 h-3" /></Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-2 text-left font-semibold">Time</th>
                <th className="px-4 py-2 text-left font-semibold">Event</th>
                <th className="px-4 py-2 text-left font-semibold">To</th>
                <th className="px-4 py-2 text-left font-semibold">Subject</th>
                <th className="px-4 py-2 text-left font-semibold">Status</th>
                <th className="px-4 py-2 text-right font-semibold"></th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500 text-sm">No mail log entries yet. Use "Send a test email" above to create the first one.</td></tr>
              ) : logs.map((l) => (
                <tr key={l.id} data-testid={`mail-log-row-${l.id}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                  <td className="px-4 py-2 text-xs text-slate-600 font-mono">{new Date(l.ts).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs"><code className="text-indigo-700">{l.event_type}</code></td>
                  <td className="px-4 py-2 text-xs text-slate-700">{(l.to || []).join(", ")}</td>
                  <td className="px-4 py-2 text-xs text-slate-900 max-w-md truncate">{l.subject}</td>
                  <td className="px-4 py-2"><StatusBadge status={l.status} /></td>
                  <td className="px-4 py-2 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setPreviewLog(l)} data-testid={`mail-log-preview-${l.id}`}>
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Preview modal */}
      <Dialog open={!!previewLog} onOpenChange={(o) => !o && setPreviewLog(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{previewLog?.subject}</DialogTitle>
            <DialogDescription>
              <span className="text-xs">{previewLog?.event_type} · {previewLog?.status} · {previewLog?.ts && new Date(previewLog.ts).toLocaleString()}</span>
              {previewLog?.resend_id && <span className="text-xs ml-2">· Resend id: <code>{previewLog.resend_id}</code></span>}
            </DialogDescription>
          </DialogHeader>
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs">
            <div><span className="text-slate-500">To:</span> {(previewLog?.to || []).join(", ")}</div>
            <div><span className="text-slate-500">From:</span> {previewLog?.from}</div>
            {previewLog?.error && <div className="text-rose-700 mt-1"><span className="text-slate-500">Error:</span> {previewLog.error}</div>}
          </div>
          {/* Phase 34c.3 — render the HTML in a sandboxed iframe so admins
              see what the recipient actually sees, instead of the raw markup.
              The stored html_preview is the first 500 chars (mail_log keeps
              the row small) — we surface a truncation note so the admin
              knows the visual may be incomplete. */}
          <div data-testid="mail-log-preview-html" className="rounded-md border border-slate-200 bg-white">
            {previewLog?.html_preview ? (
              <iframe
                title="email-preview"
                sandbox=""
                srcDoc={previewLog.html_preview}
                className="h-96 w-full rounded-md"
              />
            ) : (
              <div className="p-4 text-xs text-slate-500">(no preview captured)</div>
            )}
          </div>
          {previewLog?.html_preview && previewLog.html_preview.length >= 500 ? (
            <div className="-mt-2 text-[11px] italic text-slate-500">
              Preview truncated to first 500 chars (mail_log doesn&rsquo;t store full HTML for size reasons).
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setPreviewLog(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, tone }) {
  const toneMap = {
    emerald: "border-emerald-200 bg-emerald-50",
    rose: "border-rose-200 bg-rose-50",
    amber: "border-amber-200 bg-amber-50",
  };
  return (
    <div data-testid={`kpi-${tone}`} className={`rounded-xl border p-4 ${toneMap[tone] || "border-slate-200 bg-slate-50"}`}>
      <div className="text-[11px] uppercase tracking-wide text-slate-600 font-semibold">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value ?? 0}</div>
    </div>
  );
}
